import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Component for adding JSON-LD structured data to improve SEO
 * Based on Schema.org and Google's structured data guidelines
 */
const StructuredData = ({ projects = [] }) => {
  // Organization Schema - Describes Studio SAM as a creative organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Studio SAM",
    "alternateName": "SAM - Studio animé et mouvementé",
    "url": "https://studio-sam.fr",
    "logo": "https://studio-sam.fr/favicon.ico",
    "description": "Studio de création audiovisuelle spécialisé dans l'animation et le motion design. Création de contenus animés pour marques, agences et associations.",
    "foundingDate": "2020",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "contact@studio-sam.fr"
    },
    "sameAs": [
      "https://www.instagram.com/studio_sam_/",
      "https://www.linkedin.com/company/studio-sam/",
      "https://www.facebook.com/studiosam.fr"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "knowsAbout": [
      "Motion Design",
      "Animation",
      "Video Production",
      "Graphic Design",
      "Audiovisual Creation"
    ]
  };

  // WebSite Schema - Describes the website and enables sitelinks search box
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Studio SAM",
    "url": "https://studio-sam.fr",
    "description": "Portfolio et site officiel de Studio SAM - Studio de création audiovisuelle spécialisé dans l'animation et le motion design",
    "inLanguage": "fr-FR",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://studio-sam.fr/?s={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList Schema - Helps Google understand site structure
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://studio-sam.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "À propos",
        "item": "https://studio-sam.fr#intro"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Galerie de Projets",
        "item": "https://studio-sam.fr#projets"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Studio",
        "item": "https://studio-sam.fr#studio"
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "Contact",
        "item": "https://studio-sam.fr#contact"
      }
    ]
  };

  // CreativeWork Schema - For the portfolio/projects section
  const creativeWorkSchema = {
    "@context": "https://schema.org",
    "@type": "CreativeWorkSeries",
    "name": "Portfolio Studio SAM",
    "description": "Collection de projets d'animation et motion design réalisés par Studio SAM",
    "creator": {
      "@type": "Organization",
      "name": "Studio SAM"
    },
    "genre": ["Animation", "Motion Design", "Video Production"],
    "url": "https://studio-sam.fr#projets"
  };

  // Service Schema - Describes the services offered
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Creative Services",
    "provider": {
      "@type": "Organization",
      "name": "Studio SAM"
    },
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services de création audiovisuelle",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Motion Design",
            "description": "Création de contenus animés et motion design pour marques et entreprises"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Animation",
            "description": "Production de vidéos d'animation pour communication et marketing"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Production Audiovisuelle",
            "description": "Services de production audiovisuelle complète"
          }
        }
      ]
    }
  };

  // ContactPage Schema - Helps identify the contact section
  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Studio SAM",
    "description": "Contactez Studio SAM pour vos projets de motion design et d'animation",
    "url": "https://studio-sam.fr#contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Studio SAM",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "contact@studio-sam.fr"
      }
    }
  };

  // CollectionPage Schema - For the gallery/projects section
  const galleryPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Galerie de Projets - Studio SAM",
    "description": "Découvrez nos projets d'animation et de motion design réalisés pour marques, agences et associations",
    "url": "https://studio-sam.fr#projets",
    "mainEntity": {
      "@type": "CreativeWorkSeries",
      "name": "Portfolio Studio SAM",
      "description": "Collection de projets d'animation et motion design"
    }
  };

  return (
    <Helmet>
      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {/* WebSite Schema */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      {/* CreativeWork Schema */}
      <script type="application/ld+json">
        {JSON.stringify(creativeWorkSchema)}
      </script>
      
      {/* Service Schema */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      
      {/* ContactPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify(contactPageSchema)}
      </script>
      
      {/* Gallery/CollectionPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify(galleryPageSchema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
